import type { UserProfile } from '../../../../types/userProfile';
import { roleAlignmentScore } from '../../../../modules/recommendations/engine/scoring/dimensionScores';

import type { Job } from '../JobCard';

const ARRANGEMENT_PREFS = new Set(['remote', 'hybrid', 'onsite']);
const EMPLOYMENT_PREFS = new Set(['full_time', 'part_time', 'contract', 'internship']);

/** Common city aliases (India-focused job market). */
const CITY_ALIASES: Record<string, string[]> = {
  bengaluru: ['bengaluru', 'bangalore', 'blr'],
  bangalore: ['bangalore', 'bengaluru', 'blr'],
  mumbai: ['mumbai', 'bombay'],
  bombay: ['mumbai', 'bombay'],
  delhi: ['delhi', 'new delhi', 'ncr', 'gurugram', 'gurgaon', 'noida', 'faridabad'],
  'new delhi': ['delhi', 'new delhi', 'ncr'],
  gurugram: ['gurugram', 'gurgaon'],
  gurgaon: ['gurugram', 'gurgaon'],
  hyderabad: ['hyderabad', 'secunderabad'],
  chennai: ['chennai', 'madras'],
  madras: ['chennai', 'madras'],
  kolkata: ['kolkata', 'calcutta'],
  calcutta: ['kolkata', 'calcutta'],
  pune: ['pune', 'puné'],
};

function norm(s: string | null | undefined): string {
  return (s ?? '').toLowerCase().trim();
}

/** Strip bullets, pins, and other noise from a single city token. */
function normalizeCityToken(raw: string): string {
  return raw
    .replace(/^[\s•·\-–—*]+/, '')
    .replace(/\b\d{5,6}\b/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function isRemoteJob(job: Job): boolean {
  const loc = norm(job.location);
  return (
    job.remote === true
    || loc.includes('remote')
    || loc.includes('work from home')
    || loc.includes('wfh')
    || loc === 'anywhere'
  );
}

function jobArrangementMatches(job: Job, pref: string): boolean {
  const loc = norm(job.location);
  const jt = norm(job.job_type);

  switch (pref) {
    case 'remote':
      return isRemoteJob(job);
    case 'hybrid':
      return loc.includes('hybrid') || jt.includes('hybrid');
    case 'onsite':
      return !isRemoteJob(job) && !loc.includes('hybrid');
    default:
      return false;
  }
}

function jobEmploymentMatches(job: Job, pref: string): boolean {
  const jt = norm(job.job_type);
  if (!jt) return true;

  switch (pref) {
    case 'full_time':
      return jt.includes('full');
    case 'part_time':
      return jt.includes('part');
    case 'contract':
      return jt.includes('contract') || jt.includes('freelance');
    case 'internship':
      return jt.includes('intern');
    default:
      return false;
  }
}

export function parseLocationCities(location: string | null | undefined): string[] {
  if (!location?.trim()) return [];
  return location
    .split(/[,;|]/)
    .map((s) => normalizeCityToken(s))
    .filter((s) => s.length >= 2);
}

function expandCityTokens(city: string): string[] {
  const key = normalizeCityToken(city);
  const aliases = CITY_ALIASES[key];
  return aliases ? [...new Set([key, ...aliases])] : [key];
}

/** Strict city / region match against job location text. */
export function jobMatchesPreferredCity(job: Job, cities: string[]): boolean {
  if (cities.length === 0) return false;

  const loc = norm(job.location);
  if (!loc) return false;

  for (const city of cities) {
    for (const token of expandCityTokens(city)) {
      if (token.length >= 3 && loc.includes(token)) return true;
    }
  }
  return false;
}

/** LinkedIn / Naukri / Indeed rows ingested via search-jobs-external. */
export function isExternalChannelJob(job: Job): boolean {
  const ch = norm(job.source_channel ?? '');
  return ch === 'linkedin' || ch === 'naukri' || ch === 'indeed';
}

/** Any non-Pinecone catalog row (Peerlist cron + FETCH Live channels). */
export function isCatalogChannelJob(job: Job): boolean {
  return isPeerlistChannelJob(job) || isExternalChannelJob(job);
}

function jobLocationConflictsWithProfile(job: Job, profile: UserProfile): boolean {
  const loc = norm(job.location);
  if (!loc) return false;
  const cities = parseLocationCities(profile.location);
  if (cities.length === 0) return false;
  const indiaFocus = cities.some((c) =>
    Object.keys(CITY_ALIASES).includes(c) || c.includes('india'),
  );
  if (!indiaFocus) return false;

  if (
    loc.includes('united states')
    || loc.includes('united kingdom')
    || loc.includes('canada')
    || loc.includes('australia')
    || /\b(usa|u\.s\.|u\.k\.)\b/.test(loc)
  ) {
    return true;
  }

  // "Mountain View, CA (In-Office)" / "Austin, United States" — US city + state pattern
  if (/,\s*[a-z]{2}\b/.test(loc) && !loc.includes('india')) {
    return true;
  }

  return false;
}

/** Drop only clear geo conflicts (e.g. US listing for India-focused user). Keeps all channels. */
export function filterBlatantLocationConflicts(
  jobs: Job[],
  profile: UserProfile | null,
): Job[] {
  if (!profile) return jobs;
  return jobs.filter((job) => !jobLocationConflictsWithProfile(job, profile));
}

/**
 * Strict geographical / work-mode gate (used for ranking, not feed hard-filter).
 * Returns true when the job is allowed under the user's saved location preferences.
 */
export function jobMatchesGeoPreference(job: Job, profile: UserProfile): boolean {
  const types = profile.preferredJobTypes ?? [];
  const arrangement = types.filter((t) => ARRANGEMENT_PREFS.has(t));
  const cities = parseLocationCities(profile.location);

  const wantsRemote = arrangement.includes('remote');
  const wantsHybrid = arrangement.includes('hybrid');
  const wantsOnsite = arrangement.includes('onsite');
  const hasArrangement = arrangement.length > 0;
  const hasCities = cities.length > 0;

  if (!hasCities && !hasArrangement) {
    return true;
  }

  const loc = norm(job.location);
  if (jobLocationConflictsWithProfile(job, profile)) {
    return false;
  }

  // Naukri/LinkedIn often omit location — rank later; don't hard-drop.
  if (isExternalChannelJob(job) && !loc) {
    return true;
  }

  // Peerlist cron rows often lack parsed location.
  if (isPeerlistChannelJob(job) && !loc) {
    return true;
  }

  const remoteOk = isRemoteJob(job);
  const cityOk = hasCities && jobMatchesPreferredCity(job, cities);
  const hybridOk = wantsHybrid && jobArrangementMatches(job, 'hybrid');
  const onsiteOk = wantsOnsite && jobArrangementMatches(job, 'onsite');

  if (hasCities) {
    if (cityOk) return true;
    if (wantsRemote && remoteOk) return true;
    if (hybridOk) return true;
    return false;
  }

  if (wantsRemote && !wantsHybrid && !wantsOnsite) {
    return remoteOk;
  }

  if (hasArrangement) {
    return wantsRemote && remoteOk
      || hybridOk
      || onsiteOk;
  }

  return true;
}

function jobSalaryMatches(job: Job, expectationInr: number): boolean {
  const max = job.salary_max;
  if (max == null || !Number.isFinite(max) || max <= 0) return true;
  const currency = (job.salary_currency ?? 'INR').toUpperCase();
  // Peerlist / US listings often use USD — do not compare against INR expectation.
  if (currency !== 'INR') return true;
  return max >= expectationInr * 0.7;
}

export function hasGeoPreferences(profile: UserProfile | null): boolean {
  if (!profile) return false;
  const types = profile.preferredJobTypes ?? [];
  const hasArrangement = types.some((t) => ARRANGEMENT_PREFS.has(t));
  return hasArrangement || parseLocationCities(profile.location).length > 0;
}

/** Role targets from Account Settings profile (current role, headline, skills title). */
export function extractRoleTargets(
  profile: UserProfile | null,
  profileJobTitle?: string | null,
): string[] {
  const roles: string[] = [];
  if (profile?.currentRole?.trim()) roles.push(profile.currentRole.trim());
  if (profile?.headline?.trim()) roles.push(profile.headline.trim());
  if (profileJobTitle?.trim()) roles.push(profileJobTitle.trim());
  return [...new Set(roles)];
}

/** Normalize text for PostgREST ilike tokens (commas break `.or()` filters). */
export function normalizeRoleSearchToken(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[%_(),.;:!?'"[\]]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Build `title.ilike.%…%` OR chain safe for Supabase `.or()`. */
export function buildTitleIlikeOrFilter(tokens: string[]): string | null {
  const parts: string[] = [];
  const seen = new Set<string>();

  for (const raw of tokens) {
    const t = normalizeRoleSearchToken(raw).replace(/[%_]/g, '');
    if (t.length < 3 || t.length > 36) continue;
    if (seen.has(t)) continue;
    seen.add(t);
    parts.push(`title.ilike.%${t}%`);
    if (parts.length >= 6) break;
  }

  return parts.length > 0 ? parts.join(',') : null;
}

/** Title tokens for DB search — "Product Manager" → product manager, product, manager */
export function extractRoleSearchTokens(
  profile: UserProfile | null,
  profileJobTitle?: string | null,
): string[] {
  const STOP = new Set(['and', 'the', 'for', 'with', 'senior', 'junior', 'lead', 'staff']);
  const tokens = new Set<string>();

  for (const role of extractRoleTargets(profile, profileJobTitle)) {
    const trimmed = normalizeRoleSearchToken(role);
    const wordCount = trimmed.split(/\s+/).filter(Boolean).length;
    // Full phrase only for short job titles — not long marketing headlines (commas break OR filters).
    if (trimmed.length >= 3 && trimmed.length <= 42 && wordCount <= 5) {
      tokens.add(trimmed);
    }
    for (const w of trimmed.split(/\s+/)) {
      const clean = w.replace(/[^a-z0-9+#-]/gi, '');
      if (clean.length > 2 && !STOP.has(clean)) tokens.add(clean);
    }
  }

  return [...tokens].slice(0, 8);
}

/** Drop clear cross-domain mismatches when the user's role is known. */
export function jobPassesRolePreference(
  job: Job,
  roleTargets: string[],
): boolean {
  if (roleTargets.length === 0) return true;

  let best = 50;
  for (const role of roleTargets) {
    best = Math.max(best, roleAlignmentScore(role, undefined, job.title));
  }
  return best >= 30 || best === 50;
}

export function rolePreferenceRank(job: Job, roleTargets: string[]): number {
  if (roleTargets.length === 0) return 0;
  let best = 0;
  for (const role of roleTargets) {
    best = Math.max(best, roleAlignmentScore(role, undefined, job.title));
  }
  return best;
}

export interface ApplyUserJobPreferencesOpts {
  roleTargets?: string[];
}

/**
 * Apply Account Settings preferences + role alignment to the recommended job pool.
 */
export function applyUserJobPreferences(
  jobs: Job[],
  profile: UserProfile | null,
  opts?: ApplyUserJobPreferencesOpts,
): Job[] {
  if (!profile || jobs.length === 0) return jobs;

  const types = profile.preferredJobTypes ?? [];
  const arrangement = types.filter((t) => ARRANGEMENT_PREFS.has(t));
  const employment = types.filter((t) => EMPLOYMENT_PREFS.has(t));
  const salaryExpect = profile.salaryExpectationInr;
  const geoActive = hasGeoPreferences(profile);
  const roleTargets = opts?.roleTargets ?? extractRoleTargets(profile);

  return jobs.filter((job) => {
    if (geoActive && !jobMatchesGeoPreference(job, profile)) {
      return false;
    }
    if (arrangement.length > 0 && !geoActive) {
      if (!arrangement.some((p) => jobArrangementMatches(job, p))) return false;
    }
    if (employment.length > 0 && !employment.some((p) => jobEmploymentMatches(job, p))) {
      return false;
    }
    if (salaryExpect != null && salaryExpect > 0 && !jobSalaryMatches(job, salaryExpect)) {
      return false;
    }
    if (!jobPassesRolePreference(job, roleTargets)) {
      return false;
    }
    return true;
  });
}

/** Sort by account prefs (rank, don't hide). Matching jobs float to the top. */
export function sortJobsByPreferenceRelevance(
  jobs: Job[],
  profile: UserProfile | null,
  roleTargets: string[],
): Job[] {
  if (!profile && roleTargets.length === 0) return jobs;

  const types = profile?.preferredJobTypes ?? [];
  const employment = types.filter((t) => EMPLOYMENT_PREFS.has(t));
  const salaryExpect = profile?.salaryExpectationInr;

  const score = (job: Job): number => {
    let s = rolePreferenceRank(job, roleTargets) * 10;
    s += geoPreferenceRank(job, profile) * 20;

    if (profile && jobLocationConflictsWithProfile(job, profile)) {
      s -= 500;
    }

    if (employment.length > 0 && employment.some((p) => jobEmploymentMatches(job, p))) {
      s += 8;
    }
    if (salaryExpect != null && salaryExpect > 0 && jobSalaryMatches(job, salaryExpect)) {
      s += 5;
    }

    const loc = norm(job.location);
    if (isExternalChannelJob(job) && !loc) s += 6;
    if (isPeerlistChannelJob(job) && !loc) s += 6;
    if (loc.includes('india')) s += 4;

    return s;
  };

  return [...jobs].sort((a, b) => score(b) - score(a));
}

export function hasActiveUserJobPreferences(profile: UserProfile | null): boolean {
  if (!profile) return false;
  return (
    hasGeoPreferences(profile)
    || (profile.preferredJobTypes?.some((t) => EMPLOYMENT_PREFS.has(t)) ?? false)
    || (profile.salaryExpectationInr != null && profile.salaryExpectationInr > 0)
  );
}

/** Peerlist.io cron ingest — not LinkedIn/Naukri rows in peerlist_jobs. */
export function isPeerlistChannelJob(job: Job): boolean {
  const ch = norm(job.source_channel ?? '');
  return !ch || ch === 'peerlist';
}

/** Higher = better match for user's saved cities / work mode. */
export function geoPreferenceRank(job: Job, profile: UserProfile | null): number {
  if (!profile || !hasGeoPreferences(profile)) return 0;
  if (jobMatchesGeoPreference(job, profile)) return 3;
  const loc = norm(job.location);
  if (!loc && (isExternalChannelJob(job) || isPeerlistChannelJob(job))) return 2;
  if (loc.includes('india')) return 2;
  if (isRemoteJob(job)) return 1;
  return 0;
}

export function sortJobsByGeoPreference(jobs: Job[], profile: UserProfile | null): Job[] {
  if (!profile || !hasGeoPreferences(profile)) return jobs;
  return [...jobs].sort(
    (a, b) => geoPreferenceRank(b, profile) - geoPreferenceRank(a, profile),
  );
}

/** Round-robin by ingest channel so Peerlist is not buried below LinkedIn on page 1. */
export function balanceChannelDisplayOrder(jobs: Job[]): Job[] {
  if (jobs.length <= 1) return jobs;

  const buckets = new Map<string, Job[]>();
  for (const job of jobs) {
    const key = isPeerlistChannelJob(job)
      ? 'peerlist'
      : (job.source_channel?.toLowerCase() || 'other');
    const list = buckets.get(key) ?? [];
    list.push(job);
    buckets.set(key, list);
  }

  if (buckets.size <= 1) return jobs;

  const channelOrder = ['peerlist', 'linkedin', 'naukri', 'indeed', 'other'];
  const lists = channelOrder
    .map((k) => buckets.get(k))
    .filter((l): l is Job[] => Boolean(l?.length));

  for (const [key, list] of buckets) {
    if (!channelOrder.includes(key)) lists.push(list);
  }

  const out: Job[] = [];
  let idx = 0;
  while (out.length < jobs.length) {
    let pushed = false;
    for (const list of lists) {
      if (idx < list.length) {
        out.push(list[idx]);
        pushed = true;
      }
    }
    if (!pushed) break;
    idx += 1;
  }
  return out;
}
